import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { flujoCajaApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarUI } from '../ui/calendar';
import { toast } from '../../hooks/use-toast';
import { RefreshCw, Loader2, Table as TableIcon, ChevronLeft, ChevronRight, Info, Wallet, TrendingUp, Calendar, CalendarRange, ChevronsLeft, ChevronsRight, Minus, Plus, ArrowDownLeft, Landmark, ArrowDownCircle } from 'lucide-react';
import ProvisionesPanel from './ProvisionesPanel';
import Proyeccion12mPanel from './Proyeccion12mPanel';
import VencimientosPanel from './VencimientosPanel';
import CxCPanel from './CxCPanel';
import CxPPanel from './CxPPanel';
import FactoringPanel from './FactoringPanel';
import { FechasModificadasModal } from './FechasModificadasModal';
import { GridShiftsHistory } from './GridShiftsHistory';
import { fmtFecha } from '../../lib/utils';
import { DatePickerInput } from '../ui/date-picker';

const formatCLP = (value) => {
  if (!value || value === 0) return '';
  // Separador de miles chileno (punto) sin decimales
  return new Intl.NumberFormat('es-CL', { maximumFractionDigits: 0 }).format(value);
};

const formatCLPFull = (value) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(value || 0);

const DIAS_ES = { Mon: 'Lun', Tue: 'Mar', Wed: 'Mié', Thu: 'Jue', Fri: 'Vie', Sat: 'Sáb', Sun: 'Dom' };

const formatFecha = (iso) => {
  const [y, m, d] = iso.split('-');
  return `${d}-${m}`;
};

// Ancho de columna por modo de vista (Carlos: ajustar al mínimo en Sem/Mes
// para que toda la tabla quepa en pantalla sin scroll horizontal)
const COL_WIDTH_BY_MODE = {
  diario: 75,
  semanal: 105,
  mensual: 180,
};
const COL_WIDTH = COL_WIDTH_BY_MODE.diario;  // legacy reference (some places use static)

// Celda del grid con capacidad de desplazar (shift) su monto a otro día
function ShiftableCell({ value, fecha, colWidth, className, onShift, filaNombre }) {
  const [open, setOpen] = useState(false);
  const [dias, setDias] = useState(1);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const doShift = async (signOrCustom) => {
    setSaving(true);
    const d = typeof signOrCustom === 'number' ? signOrCustom : (signOrCustom === '+' ? dias : -dias);
    const ok = await onShift(d);
    setSaving(false);
    if (ok) setOpen(false);
  };

  // Calcula días entre `fecha` (origen, formato YYYY-MM-DD) y una Date elegida.
  // Devuelve `null` si no hay movimiento.
  const shiftToDate = async (target) => {
    if (!target) return;
    const [y, m, d] = fecha.split('-').map(Number);
    const origen = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
    const targetUTC = new Date(Date.UTC(target.getFullYear(), target.getMonth(), target.getDate()));
    const diffDays = Math.round((targetUTC - origen) / 86400000);
    if (diffDays === 0) {
      setPickerOpen(false);
      return;
    }
    setSaving(true);
    const ok = await onShift(diffDays);
    setSaving(false);
    if (ok) {
      setPickerOpen(false);
      setOpen(false);
    }
  };

  return (
    <td
      className={`border text-right tabular-nums ${className} cursor-pointer hover:ring-2 hover:ring-amber-400 hover:ring-inset transition-all`}
      style={{ width: colWidth, minWidth: colWidth }}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="w-full h-full text-right pr-1 focus:outline-none"
            title={`${filaNombre} · ${fecha} · ${formatCLPFull(value)}\nClic para mover a otro día`}
            data-testid={`cell-${fecha}`}
          >
            {formatCLP(value)}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-3" align="center" onClick={(e) => e.stopPropagation()}>
          <div className="space-y-2">
            <div className="text-xs font-semibold truncate">{filaNombre}</div>
            <div className="text-xs text-muted-foreground">
              {fecha} · <span className="tabular-nums font-medium text-foreground">{formatCLPFull(value)}</span>
            </div>
            <div className="border-t pt-2">
              <div className="text-[11px] text-muted-foreground mb-1">Mover monto completo:</div>
              <div className="flex items-center gap-1 mb-2">
                <Button size="sm" variant="outline" className="h-7 flex-1" onClick={() => doShift(-1)} disabled={saving} data-testid="shift-back-1">
                  <ChevronLeft className="w-3.5 h-3.5" /> 1d
                </Button>
                <Button size="sm" variant="outline" className="h-7 flex-1" onClick={() => doShift(-7)} disabled={saving} data-testid="shift-back-7">
                  <ChevronsLeft className="w-3.5 h-3.5" /> 1s
                </Button>
                <Button size="sm" variant="outline" className="h-7 flex-1" onClick={() => doShift(7)} disabled={saving} data-testid="shift-fwd-7">
                  1s <ChevronsRight className="w-3.5 h-3.5" />
                </Button>
                <Button size="sm" variant="outline" className="h-7 flex-1" onClick={() => doShift(1)} disabled={saving} data-testid="shift-fwd-1">
                  1d <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="text-[11px] text-muted-foreground mb-1">Personalizado (días ± ):</div>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setDias((n) => Math.max(1, n - 1))}>
                  <Minus className="w-3 h-3" />
                </Button>
                <Input
                  type="number"
                  value={dias}
                  onChange={(e) => setDias(Math.max(1, Math.min(365, Number(e.target.value) || 1)))}
                  className="h-7 text-center"
                  data-testid="shift-custom-input"
                />
                <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setDias((n) => Math.min(365, n + 1))}>
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
              <div className="flex gap-1 mt-1">
                <Button size="sm" variant="secondary" className="h-7 flex-1" onClick={() => doShift('-')} disabled={saving} data-testid="shift-back-custom">
                  ← {dias}d atrás
                </Button>
                <Button size="sm" className="h-7 flex-1 bg-blue-600 hover:bg-blue-700" onClick={() => doShift('+')} disabled={saving} data-testid="shift-fwd-custom">
                  {dias}d adelante →
                </Button>
              </div>
            </div>
            <div className="border-t pt-2">
              <div className="text-[11px] text-muted-foreground mb-1">O elegí una fecha exacta:</div>
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    size="sm" variant="outline"
                    className="h-7 w-full justify-center"
                    disabled={saving}
                    data-testid="shift-pick-date"
                  >
                    <Calendar className="w-3.5 h-3.5 mr-1" />
                    Mover a fecha…
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="center" onClick={(e) => e.stopPropagation()}>
                  <CalendarUI
                    mode="single"
                    selected={undefined}
                    onSelect={shiftToDate}
                    initialFocus
                    weekStartsOn={1}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </td>
  );
}

export default function FlujoCajaGrid() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [openProvisiones, setOpenProvisiones] = useState(false);
  const [openProyeccion, setOpenProyeccion] = useState(false);
  const [openVencimientos, setOpenVencimientos] = useState(false);
  const [fechasModalOpen, setFechasModalOpen] = useState(false);
  const [openCxC, setOpenCxC] = useState(false);
  const [openFactoring, setOpenFactoring] = useState(false);
  // Vista del grid (Carlos): ocultar fines de semana + Diario/Semanal/Mensual
  const [hideWeekends, setHideWeekends] = useState(true);
  const [viewMode, setViewMode] = useState('diario'); // 'diario' | 'semanal' | 'mensual'
  // Modal detalle de CxC al pinchar celda de fila 43
  const [cxcModal, setCxcModal] = useState({ open: false, fecha: null, fechas: null, items: [], loading: false });
  // Modal detalle de CxP al pinchar celda de fila 2 (Proveedores Ctas x Pagar)
  const [cxpModal, setCxpModal] = useState({ open: false, fecha: null });
  // Modal detalle de facturas factorizadas vencidas (filas 50/53 saldo > 0)
  const [vencidasModal, setVencidasModal] = useState({
    open: false, lineaId: null, lineaNombre: '', items: [], loading: false, totalVencido: 0,
  });
  const scrollRef = useRef(null);

  const loadGrid = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const res = await flujoCajaApi.getGrid(opts);
      setData(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || 'Error al cargar grid';
      toast({ title: msg, variant: 'destructive' });
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Al cerrar el modal de provisiones, recargar el grid SIN cache (dato mutó)
  const handleCloseProvisiones = (isOpen) => {
    setOpenProvisiones(isOpen);
    if (!isOpen) loadGrid({ nocache: true });
  };

  // Idem para CxC (afectan row 44)
  const handleCloseCxC = (isOpen) => {
    setOpenCxC(isOpen);
    if (!isOpen) loadGrid({ nocache: true });
  };

  // Idem para Proyección (afecta inyecciones y supresiones en múltiples filas)
  const handleCloseProyeccion = (isOpen) => {
    setOpenProyeccion(isOpen);
    if (!isOpen) loadGrid({ nocache: true });
  };

  // Idem para Factoring (afecta filas 48, 50, 51, 53, 54)
  const handleCloseFactoring = (isOpen) => {
    setOpenFactoring(isOpen);
    if (!isOpen) loadGrid({ nocache: true });
  };

  // Crear un shift (desplazar monto de un día a otro) y recargar el grid
  const crearShift = async (orden, fechaOrigen, diasShift, monto, nota = '') => {
    if (!diasShift || Math.abs(diasShift) > 365) {
      toast({ title: 'Días fuera de rango (1-365)', variant: 'destructive' });
      return false;
    }
    try {
      await flujoCajaApi.createGridShift({
        orden, fecha_origen: fechaOrigen, dias_shift: diasShift, monto, nota,
      });
      toast({ title: diasShift > 0 ? `Movido +${diasShift}d adelante` : `Movido ${diasShift}d atrás` });
      await loadGrid();
      return true;
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
      return false;
    }
  };

  // Abrir modal con detalle de CxC para una fecha (o rango de fechas si Sem/Mes)
  const openCxCModal = async (group) => {
    const fechasGrupo = group.indices.map(i => rawFechas[i].fecha);
    const setFechas = new Set(fechasGrupo);
    setCxcModal({ open: true, fecha: group.fecha, fechas: fechasGrupo, items: [], loading: true });
    try {
      const res = await flujoCajaApi.listCxC('pendiente');
      const all = res?.data?.items || [];
      const matching = all.filter(c => setFechas.has(c.fecha_venc));
      matching.sort((a, b) => (b.monto || 0) - (a.monto || 0));
      setCxcModal({ open: true, fecha: group.fecha, fechas: fechasGrupo, items: matching, loading: false });
    } catch (err) {
      toast({ title: 'No se pudo cargar el detalle', description: err?.message || '', variant: 'destructive' });
      setCxcModal(prev => ({ ...prev, loading: false }));
    }
  };

  const moverCxC = async (cxcId, nuevaFecha) => {
    try {
      const res = await flujoCajaApi.cambiarFechaCxC(cxcId, nuevaFecha);
      const moved = res?.data?._moved_from_weekend;
      const finalDate = res?.data?.fecha_venc || nuevaFecha;
      toast({
        title: moved ? `Movida al lunes (${finalDate})` : `Movida a ${finalDate}`,
        description: moved ? `${moved} era fin de semana → se ajustó automáticamente.` : '',
      });
      await loadGrid();
      // Refrescar la lista en el modal
      if (cxcModal.fechas?.length) {
        const all = (await flujoCajaApi.listCxC('pendiente'))?.data?.items || [];
        const setFechas = new Set(cxcModal.fechas);
        const matching = all.filter(c => setFechas.has(c.fecha_venc)).sort((a, b) => (b.monto || 0) - (a.monto || 0));
        setCxcModal(prev => ({ ...prev, items: matching }));
      }
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al mover CxC', variant: 'destructive' });
    }
  };

  // Abrir modal con facturas factorizadas vencidas de una línea (fila 50/53 con saldo > 0)
  const openVencidasModal = async (fila) => {
    const lineaId = fila.factoring_linea_id;
    if (!lineaId) {
      toast({ title: 'Esta línea no tiene línea_id asociada', variant: 'destructive' });
      return;
    }
    setVencidasModal({
      open: true, lineaId, lineaNombre: fila.factoring_linea_nombre || fila.nombre,
      items: [], loading: true, totalVencido: fila.saldo || 0,
    });
    try {
      const res = await flujoCajaApi.listFactoringAsignaciones({ linea_id: lineaId, solo_vencidas: true });
      const items = (res?.data?.items || []).sort((a, b) => (a.fecha_pago_efectiva || a.fecha_venc || '').localeCompare(b.fecha_pago_efectiva || b.fecha_venc || ''));
      setVencidasModal(prev => ({ ...prev, items, loading: false, totalVencido: res?.data?.total_vencido || prev.totalVencido }));
    } catch (err) {
      toast({ title: 'No se pudieron cargar las vencidas', description: err?.message || '', variant: 'destructive' });
      setVencidasModal(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => { loadGrid(); }, [loadGrid]);

  // Cuando cargan los datos, scroll automático para que el día HOY sea la primera columna visible
  useEffect(() => {
    if (!data || !scrollRef.current) return;
    // Buscar el grupo (post-filtro/agrupación) que contiene HOY
    const containerEl = scrollRef.current;
    const targetEl = containerEl.querySelector('[data-testid^="header-"][class*="amber"]');
    if (targetEl) {
      const rect = targetEl.getBoundingClientRect();
      const ctRect = containerEl.getBoundingClientRect();
      containerEl.scrollLeft += rect.left - ctRect.left - 440; // 440 = ancho cols sticky
    }
  }, [data, hideWeekends, viewMode]);

  const stylesByTipo = useMemo(() => ({
    egreso: {
      row: 'bg-red-50/40 dark:bg-red-950/10 hover:bg-red-100/60',
      stickyBg: 'bg-red-50 dark:bg-red-950/80',
      label: 'text-red-900 dark:text-red-200',
      value: 'text-red-700 dark:text-red-300',
      badge: 'bg-red-100 text-red-800 border-red-200',
    },
    ingreso: {
      row: 'bg-emerald-50/40 dark:bg-emerald-950/10 hover:bg-emerald-100/60',
      stickyBg: 'bg-emerald-50 dark:bg-emerald-950/80',
      label: 'text-emerald-900 dark:text-emerald-200',
      value: 'text-emerald-700 dark:text-emerald-300',
      badge: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    },
    total: {
      row: 'bg-slate-200/70 dark:bg-slate-800/40 hover:bg-slate-300/70 font-bold',
      stickyBg: 'bg-slate-200 dark:bg-slate-800',
      label: 'text-slate-900 dark:text-slate-100 font-bold',
      value: 'text-slate-900 dark:text-slate-100 font-bold',
      badge: 'bg-slate-300 text-slate-900 border-slate-400',
    },
    solicitar: {
      row: 'bg-amber-100/70 dark:bg-amber-900/30 hover:bg-amber-200/80 dark:hover:bg-amber-900/40 font-extrabold border-y-2 border-amber-500',
      stickyBg: 'bg-amber-200 dark:bg-amber-900',
      label: 'text-amber-950 dark:text-amber-100 font-extrabold uppercase tracking-wide',
      value: 'text-amber-950 dark:text-amber-100 font-extrabold tabular-nums',
      badge: 'bg-amber-300 text-amber-950 border-amber-500',
    },
    banco: {
      row: 'bg-blue-50/60 dark:bg-blue-950/15 hover:bg-blue-100/70 font-semibold',
      stickyBg: 'bg-blue-50 dark:bg-blue-950/80',
      label: 'text-blue-900 dark:text-blue-200 font-semibold',
      value: 'text-blue-700 dark:text-blue-300 font-semibold',
      badge: 'bg-blue-100 text-blue-800 border-blue-200',
    },
    separador: {
      row: 'bg-black dark:bg-black',
      stickyBg: 'bg-black dark:bg-black',
      label: 'text-black dark:text-black',
      value: 'text-black dark:text-black',
      badge: '',
    },
  }), []);

  const scrollBy = (offset) => {
    if (scrollRef.current) scrollRef.current.scrollLeft += offset;
  };

  if (loading) {
    return (
      <Card data-testid="grid-loading">
        <CardHeader><Skeleton className="h-6 w-64" /></CardHeader>
        <CardContent><Skeleton className="h-96" /></CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card data-testid="grid-no-data">
        <CardContent className="py-12 text-center text-muted-foreground">
          <TableIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No hay datos disponibles. Agregue provisiones, proyecciones, CxC y saldos bancarios desde los paneles superiores.</p>
        </CardContent>
      </Card>
    );
  }

  const { fechas: rawFechas, filas: rawFilas, total_egresos_dia: rawTotEg, total_ingresos_dia: rawTotIng, meta } = data;
  const totalVencidoEgresos = data.total_vencido_egresos || 0;

  // Carlos pidió ocultar fila 47 (Saldos L1+L2 BChile-GASTOS, redundante)
  // y destacar fila 45 (BChile saldo - GASTOS) sobre las demás.
  const filasFiltered = rawFilas.filter(f => f.orden !== 47);

  // ─── Agrupador de columnas: ocultar fines de semana y/o agrupar semana/mes ───
  const buildView = () => {
    let activeIdx = rawFechas.map((_, i) => i);
    if (hideWeekends) {
      activeIdx = activeIdx.filter(i => !rawFechas[i].es_fin_semana);
    }
    let groups = [];
    if (viewMode === 'diario') {
      groups = activeIdx.map(i => ({
        col: rawFechas[i].col,
        fecha: rawFechas[i].fecha,
        dia_semana: rawFechas[i].dia_semana,
        es_hoy: rawFechas[i].es_hoy,
        es_fin_semana: rawFechas[i].es_fin_semana,
        label: null,
        indices: [i],
      }));
    } else if (viewMode === 'semanal') {
      const map = new Map();
      activeIdx.forEach(i => {
        const d = new Date(rawFechas[i].fecha + 'T00:00:00');
        const wd = (d.getDay() + 6) % 7;
        const monday = new Date(d);
        monday.setDate(d.getDate() - wd);
        const key = monday.toISOString().split('T')[0];
        if (!map.has(key)) {
          map.set(key, {
            col: `W-${key}`,
            fecha: key,
            dia_semana: 'Sem',
            es_hoy: false,
            es_fin_semana: false,
            label: `Sem ${String(monday.getDate()).padStart(2, '0')}-${String(monday.getMonth() + 1).padStart(2, '0')}`,
            indices: [],
          });
        }
        map.get(key).indices.push(i);
        if (rawFechas[i].es_hoy) map.get(key).es_hoy = true;
      });
      groups = Array.from(map.values()).sort((a, b) => a.fecha.localeCompare(b.fecha));
    } else if (viewMode === 'mensual') {
      const map = new Map();
      const meses_es = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
      activeIdx.forEach(i => {
        const [y, m] = rawFechas[i].fecha.split('-');
        const key = `${y}-${m}`;
        if (!map.has(key)) {
          map.set(key, {
            col: `M-${key}`,
            fecha: `${y}-${m}-01`,
            dia_semana: 'Mes',
            es_hoy: false,
            es_fin_semana: false,
            label: `${meses_es[parseInt(m, 10) - 1]} ${y.slice(-2)}`,
            indices: [],
          });
        }
        map.get(key).indices.push(i);
        if (rawFechas[i].es_hoy) map.get(key).es_hoy = true;
      });
      groups = Array.from(map.values()).sort((a, b) => a.fecha.localeCompare(b.fecha));
    }
    // Reducir cada fila/total a las columnas agrupadas:
    // - Filas de FLUJO (tipo egreso/ingreso): SUMAR los días del bucket
    // - Filas de SALDO ACUMULADO (banco/total/saldo_calculado): tomar el ÚLTIMO día del bucket
    //   Carlos: el resultado al cierre del mes/semana debe coincidir con la vista diaria.
    const reduceFlow = (vals) => groups.map(g => g.indices.reduce((sum, i) => sum + (vals[i] || 0), 0));
    const reduceLast = (vals) => groups.map(g => {
      const lastIdx = g.indices[g.indices.length - 1];
      return vals[lastIdx] || 0;
    });
    const isCumulative = (f) => (
      f.tipo === 'banco' ||
      f.tipo === 'total' ||
      f.saldo_calculado === true ||
      f.orden === 41 || f.orden === 44 || f.orden === 45 || f.orden === 47 || f.orden === 48
    );
    const newFilas = filasFiltered.map(f => ({
      ...f,
      valores: isCumulative(f) ? reduceLast(f.valores) : reduceFlow(f.valores),
    }));
    return {
      fechas: groups,
      filas: newFilas,
      total_egresos_dia: reduceFlow(rawTotEg),  // flujo
      total_ingresos_dia: reduceFlow(rawTotIng),  // flujo
    };
  };
  const { fechas, filas, total_egresos_dia, total_ingresos_dia } = buildView();
  const saldoNeto = total_ingresos_dia.map((v, i) => v - total_egresos_dia[i]);
  // Ancho dinámico de columna según modo de vista
  const colW = COL_WIDTH_BY_MODE[viewMode] || COL_WIDTH_BY_MODE.diario;

  // Carlos: alerta de déficit en fila 57 (con click → scroll a esa fecha)
  const row57 = filas.find(f => f.orden === 57);
  const deficitMeta = row57?.meta || null;
  const hasDeficit = deficitMeta?.first_deficit_date;

  const scrollToFecha = (fechaStr) => {
    if (!fechaStr || !scrollRef.current) return;
    // Buscar el grupo (post-filtro) que contiene esta fecha
    const grupoIdx = fechas.findIndex(g =>
      g.indices.some(idx => rawFechas[idx]?.fecha === fechaStr)
    );
    if (grupoIdx < 0) return;
    // El offset sticky de las 4 cols (#, Concepto, Total, S.Vencido) es 440px
    const targetX = grupoIdx * colW;
    scrollRef.current.scrollLeft = targetX;
    // Pulse effect en el header de esa columna
    const headerCell = scrollRef.current.querySelector(`[data-testid="header-${fechas[grupoIdx].col}"]`);
    if (headerCell) {
      headerCell.classList.add('flash-target');
      setTimeout(() => headerCell.classList.remove('flash-target'), 2000);
    }
  };

  return (
    <Card data-testid="flujo-caja-grid-card" className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <TableIcon className="w-4 h-4" /> Cuadro Día a Día — Proyección 91 días
            </CardTitle>
            <CardDescription>
              {meta.filas_count} cuentas × {fechas.length} {viewMode === 'mensual' ? 'meses' : viewMode === 'semanal' ? 'semanas' : 'días'}
              {' · '}
              Rango: <Badge variant="outline" className="text-xs">{meta.fecha_inicio} → {meta.fecha_fin}</Badge>
              {meta.provisiones_activas_en_ventana > 0 && (
                <Badge variant="secondary" className="ml-2 text-xs">{meta.provisiones_activas_en_ventana} provisiones</Badge>
              )}
              {meta.cxc_en_ventana > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs bg-indigo-100 text-indigo-800">{meta.cxc_en_ventana} CxC</Badge>
              )}
              {meta.proyeccion_inyecciones > 0 && (
                <Badge variant="outline" className="ml-1 text-xs border-emerald-400 text-emerald-700">{meta.proyeccion_inyecciones} proyección</Badge>
              )}
              {meta.shifts_aplicados > 0 && (
                <Badge variant="outline" className="ml-1 text-xs border-blue-400 text-blue-700 dark:text-blue-300">
                  {meta.shifts_aplicados} shift{meta.shifts_aplicados !== 1 ? 's' : ''} aplicado{meta.shifts_aplicados !== 1 ? 's' : ''}
                </Badge>
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <GridShiftsHistory onChange={() => loadGrid({ nocache: true })} />
            <Button
              variant="default"
              size="sm"
              onClick={() => setOpenProvisiones(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="btn-open-provisiones"
            >
              <Wallet className="w-4 h-4 mr-1.5" /> Provisiones
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => setOpenProyeccion(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="btn-open-proyeccion"
            >
              <TrendingUp className="w-4 h-4 mr-1.5" /> Proyección 12m
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => setOpenCxC(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="btn-open-cxc"
            >
              <ArrowDownLeft className="w-4 h-4 mr-1.5" /> CxC Clientes
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => setOpenFactoring(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="btn-open-factoring"
            >
              <Landmark className="w-4 h-4 mr-1.5" /> Factoring
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => setOpenVencimientos(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="btn-open-vencimientos"
            >
              <Calendar className="w-4 h-4 mr-1.5" /> Vencimientos
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setFechasModalOpen(true)}
              className="border-blue-300 text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/30"
              title="Auditoría de CxC y factoring con fecha de pago modificada"
              data-testid="btn-open-fechas-modificadas"
            >
              <CalendarRange className="w-4 h-4 mr-1.5" /> Fechas Modificadas
            </Button>
            <div className="w-px h-5 bg-slate-300 dark:bg-slate-700 mx-1" />
            <Button variant="outline" size="sm" onClick={() => scrollBy(-colW * 7)} data-testid="btn-scroll-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => scrollBy(colW * 7)} data-testid="btn-scroll-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => loadGrid({ nocache: true })} data-testid="btn-refresh-grid" title="Refrescar (sin cache)">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
            <div className="w-px h-5 bg-slate-300 dark:bg-slate-700 mx-1" />
            {/* Toggle ocultar fines de semana */}
            <Button
              variant={hideWeekends ? 'default' : 'outline'}
              size="sm"
              onClick={() => setHideWeekends(!hideWeekends)}
              className={hideWeekends ? 'bg-slate-700 hover:bg-slate-800 text-white' : ''}
              data-testid="btn-toggle-weekends"
              title={hideWeekends ? 'Mostrar sábados y domingos' : 'Ocultar fines de semana'}
            >
              {hideWeekends ? 'L-V' : 'L-D'}
            </Button>
            {/* Selector de vista Diario / Semanal / Mensual */}
            <div className="inline-flex rounded-md border bg-white dark:bg-slate-900" data-testid="view-mode-selector">
              {[
                { v: 'diario', label: 'Día' },
                { v: 'semanal', label: 'Sem' },
                { v: 'mensual', label: 'Mes' },
              ].map(opt => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setViewMode(opt.v)}
                  className={`px-2.5 h-8 text-xs font-semibold transition-colors first:rounded-l-md last:rounded-r-md border-r last:border-r-0 ${
                    viewMode === opt.v
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                  }`}
                  data-testid={`view-mode-${opt.v}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Leyenda */}
        <div className="flex flex-wrap gap-2 mt-2 text-xs">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-red-300" /> Egresos / Por pagar</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-300" /> Ingresos / Por cobrar</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-blue-300" /> Bancos / Saldos HOY</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-slate-400" /> Totales / Subtotales</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-400 ring-2 ring-amber-600" /> Saldo Global</span>
          {/* Iter 313.k.2 — semáforo del colchón diario */}
          <span className="flex items-center gap-1 border-l pl-2 ml-1">
            <span className="text-[10px] text-gray-500">Colchón día:</span>
            <span className="w-3 h-3 rounded-sm bg-emerald-200" title="> $30M — holgado" />
            <span className="text-[10px]">&gt;30M</span>
            <span className="w-3 h-3 rounded-sm bg-amber-200" title="$0 a $30M — tenso" />
            <span className="text-[10px]">0-30M</span>
            <span className="w-3 h-3 rounded-sm bg-rose-200" title="< $0 — déficit" />
            <span className="text-[10px]">&lt;0</span>
          </span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-amber-400 ring-2 ring-amber-500" /> Hoy</span>
          <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-600" /> S. Vencido &gt; 0</span>
          <span className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded bg-red-100 dark:bg-red-950 text-red-800 dark:text-red-200 font-semibold" data-testid="total-vencido-egresos-banner">
            Total vencido egresos: {formatCLPFull(totalVencidoEgresos)}
          </span>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {hasDeficit && (
          <button
            type="button"
            onClick={() => scrollToFecha(deficitMeta.first_deficit_date)}
            className="w-full bg-rose-50 dark:bg-rose-950/30 border-y border-rose-300 dark:border-rose-800 px-4 py-2 flex items-center justify-between gap-2 hover:bg-rose-100 dark:hover:bg-rose-950/50 transition-colors group"
            data-testid="deficit-alert-banner"
          >
            <div className="flex items-center gap-2 text-sm">
              <span className="text-base">⚠️</span>
              <span className="font-semibold text-rose-800 dark:text-rose-200">
                Déficit proyectado de {formatCLPFull(deficitMeta.first_deficit_amount)}
              </span>
              <span className="text-rose-700 dark:text-rose-300">
                a partir del <strong>{deficitMeta.first_deficit_date}</strong>
              </span>
              {deficitMeta.peak_deficit_idx !== deficitMeta.first_deficit_idx && (
                <span className="text-xs text-rose-600 dark:text-rose-400 ml-2">
                  · Monto y fecha crítica: {formatCLPFull(deficitMeta.peak_deficit_amount)} el {deficitMeta.peak_deficit_date}
                </span>
              )}
            </div>
            <span className="text-xs text-rose-700 dark:text-rose-300 group-hover:underline">
              Click para ir a esa fecha →
            </span>
          </button>
        )}
        <div ref={scrollRef} className="overflow-x-auto overflow-y-auto h-[calc(100vh-220px)] border-t" data-testid="grid-scroll-container">
          <table className="text-[11px] border-separate" style={{ minWidth: `${440 + fechas.length * colW}px`, borderSpacing: 0 }}>
            <thead className="sticky top-0 z-30 bg-white dark:bg-slate-950">
              {/* Fila 1: Fechas (sticky top — Carlos: queden fijas mientras scrolleas filas inferiores) */}
              <tr>
                <th className="sticky left-0 z-40 bg-slate-100 dark:bg-slate-900 border px-1 py-0.5 text-center w-10 min-w-[40px]" title="N° fila (orden)">#</th>
                <th className="sticky left-10 z-40 bg-slate-100 dark:bg-slate-900 border px-1.5 py-0.5 text-left w-48 min-w-[192px]">Concepto</th>
                <th className="sticky left-[202px] z-40 bg-slate-100 dark:bg-slate-900 border px-1.5 py-0.5 text-right w-28 min-w-[112px]">Total</th>
                <th className="sticky left-[314px] z-40 bg-slate-100 dark:bg-slate-900 border px-1.5 py-0.5 text-right w-28 min-w-[112px]" title="Saldo Vencido (past due)">S. Vencido</th>
                {fechas.map((f) => (
                  <th
                    key={f.col}
                    className={`border px-0.5 py-0.5 text-center font-medium ${
                      f.es_hoy ? 'bg-amber-200 dark:bg-amber-900 ring-2 ring-amber-500 ring-inset' :
                      f.es_fin_semana ? 'bg-slate-200 dark:bg-slate-800 text-slate-500' :
                      'bg-slate-100 dark:bg-slate-900'
                    }`}
                    style={{ width: colW, minWidth: colW }}
                    data-testid={`header-${f.col}`}
                    title={`${f.col} · ${f.fecha}`}
                  >
                    <div className="text-[9px] text-slate-500 dark:text-slate-400 leading-tight">{DIAS_ES[f.dia_semana] || f.dia_semana}</div>
                    <div className="tabular-nums leading-tight">{f.label || formatFecha(f.fecha)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filas.map((fila) => {
                const st = stylesByTipo[fila.tipo];
                const isSep = fila.tipo === 'separador';
                const cellPad = isSep ? 'p-0 h-2 leading-none' : 'px-1.5 py-0.5';
                // Fila 45: BChile saldo - GASTOS → destacada (Carlos)
                const isHighlight45 = fila.orden === 45;
                // Iter 293.j (Carlos, feb-2026): fila 48 Disp.Factoring → fondo verdoso (línea informativa)
                const isHighlight48 = fila.orden === 48;
                // Carlos: fila 57 destacada en rojo cuando hay déficit
                const isAlert57 = fila.orden === 57 && fila.total > 0;
                const rowExtra = isHighlight45
                  ? 'ring-2 ring-blue-500 ring-inset bg-blue-100/80 dark:bg-blue-900/40 font-extrabold shadow-sm'
                  : isHighlight48
                  ? 'bg-emerald-50 dark:bg-emerald-950/40 border-y border-emerald-200 dark:border-emerald-800'
                  : isAlert57
                  ? 'ring-2 ring-rose-500 ring-inset bg-rose-100/70 dark:bg-rose-900/40 font-bold animate-pulse-slow'
                  : '';
                const stickyExtra = isHighlight45
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100 font-extrabold'
                  : isHighlight48
                  ? 'bg-emerald-100 dark:bg-emerald-900/60 text-emerald-900 dark:text-emerald-100'
                  : isAlert57
                  ? 'bg-rose-100 dark:bg-rose-900 text-rose-900 dark:text-rose-100 font-bold'
                  : '';
                const valueExtra = isHighlight45
                  ? 'text-blue-900 dark:text-blue-100 font-extrabold'
                  : isHighlight48
                  ? 'text-emerald-800 dark:text-emerald-200'
                  : isAlert57
                  ? 'text-rose-700 dark:text-rose-300 font-bold'
                  : '';
                return (
                  <tr key={`${fila.orden}-${fila.nro}`} className={`${st.row} ${rowExtra} transition-colors`} data-testid={`row-orden-${fila.orden}`}>
                    <td className={`sticky left-0 z-20 border border-r-2 border-r-slate-300 dark:border-r-slate-700 tabular-nums ${cellPad} ${stickyExtra || st.stickyBg} ${stickyExtra || st.label} text-center font-mono text-[10px]`}
                        title={isSep ? '' : `Fila ${fila.orden}${fila.nro ? ' · ' + fila.nro.trim() : ''}`}>
                      {isSep ? '' : (
                        // Iter 236.o+p: orden fraccional ⇒ usar el primer decimal como letra
                        // 43.5 → "43b" · 2.1 → "2a" · 2.2 → "2b"
                        Number.isInteger(fila.orden)
                          ? fila.orden
                          : `${Math.floor(fila.orden)}${String.fromCharCode(96 + Math.round((fila.orden - Math.floor(fila.orden)) * 10))}`
                      )}
                    </td>
                    <td className={`sticky left-10 z-20 border truncate max-w-[192px] ${cellPad} ${stickyExtra || st.stickyBg} ${stickyExtra || st.label}`}
                        title={isSep ? '' : fila.nombre}>
                      {isSep ? '' : (
                        <span className="inline-flex items-center gap-1 truncate">
                          {fila.from_proyeccion && (
                            <Badge variant="outline" className="text-[9px] py-0 px-1 h-3.5 border-indigo-400 text-indigo-700 dark:text-indigo-300">12m</Badge>
                          )}
                          <span className="truncate">{fila.nombre || '—'}</span>
                        </span>
                      )}
                    </td>
                    <td className={`sticky left-[202px] z-20 border text-right tabular-nums ${cellPad} ${stickyExtra || st.stickyBg} ${valueExtra || st.value}`}>
                      {isSep ? '' : (
                        fila.orden === 57 && fila.total > 0 && fila.meta?.first_deficit_date ? (
                          <button
                            type="button"
                            onClick={() => scrollToFecha(fila.meta.first_deficit_date)}
                            className="inline-flex flex-col items-end leading-tight hover:bg-rose-200/50 dark:hover:bg-rose-900/40 px-1 rounded transition w-full"
                            data-testid="row57-total-link"
                            title={`Click para ir al ${fila.meta.first_deficit_date}`}
                          >
                            <span className="font-bold text-rose-700 dark:text-rose-300">{formatCLP(fila.total)}</span>
                            <span className="text-[9px] text-rose-600 dark:text-rose-400 underline">{fila.meta.first_deficit_date}</span>
                          </button>
                        ) : formatCLP(fila.total)
                      )}
                    </td>
                    <td className={`sticky left-[314px] z-20 border border-r-2 border-r-slate-400 dark:border-r-slate-600 text-right tabular-nums ${cellPad} ${stickyExtra || st.stickyBg} ${valueExtra || st.value}`}
                        title={fila.saldo > 0 ? `Saldo vencido: ${formatCLPFull(fila.saldo)}` : ''}>
                      {isSep ? '' : (() => {
                        // Factoring asignado con vencido > 0 → rojo + clickeable a modal de facturas vencidas
                        const isFactoringVencido = fila.saldo > 0 && fila.factoring_rol === 'asignado';
                        if (isFactoringVencido) {
                          return (
                            <button
                              type="button"
                              onClick={() => openVencidasModal(fila)}
                              className="inline-flex items-center gap-1 justify-end w-full text-red-700 dark:text-red-300 font-semibold hover:bg-red-100 dark:hover:bg-red-900/40 hover:underline px-1 rounded transition cursor-pointer"
                              title={`Click para ver ${formatCLPFull(fila.saldo)} en facturas vencidas`}
                              data-testid={`factoring-vencido-${fila.orden}`}
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-red-600 flex-shrink-0" />
                              {formatCLP(fila.saldo)}
                            </button>
                          );
                        }
                        return (
                          <span className={`inline-flex items-center gap-1 justify-end w-full ${fila.saldo > 0 && fila.tipo === 'egreso' ? 'text-red-700 dark:text-red-300 font-semibold' : ''}`}>
                            {fila.saldo > 0 && fila.tipo === 'egreso' && (
                              <span className="w-1.5 h-1.5 rounded-full bg-red-600 flex-shrink-0" />
                            )}
                            {formatCLP(fila.saldo)}
                          </span>
                        );
                      })()}
                    </td>
                    {fila.valores.map((v, i) => {
                      const f = fechas[i];
                      const highlight = f.es_hoy ? 'ring-2 ring-amber-500 ring-inset bg-amber-50 dark:bg-amber-950/20' :
                                        f.es_fin_semana ? 'bg-slate-50 dark:bg-slate-900/30' : '';
                      const shiftable = !isSep && Math.abs(v) > 0 && ['egreso', 'ingreso'].includes(fila.tipo) && viewMode === 'diario';
                      const isNegativeBelow40 = fila.orden >= 40 && v < 0;
                      // Importante: usar `!` (important) para pisar text-slate-* que viene en st.value
                      const negClass = isNegativeBelow40 ? '!text-red-600 dark:!text-red-400 font-bold' : '';
                      // Carlos: en fila 43 (CxC), al pinchar se abre detalle de facturas
                      const isCxCCell = fila.orden === 43 && Math.abs(v) > 0 && !isSep;
                      // Carlos: en fila 2 (CxP/Proveedores), al pinchar abre nómina del día
                      const isCxPCell = fila.orden === 2 && Math.abs(v) > 0 && !isSep;
                      if (isCxCCell) {
                        return (
                          <td
                            key={i}
                            className={`border text-right tabular-nums ${cellPad} ${valueExtra || st.value} ${highlight} ${negClass} cursor-pointer hover:bg-emerald-100 dark:hover:bg-emerald-900/30 hover:ring-1 hover:ring-emerald-500 transition-colors`}
                            style={{ width: colW, minWidth: colW }}
                            title={`${formatCLPFull(v)} — Click para ver facturas`}
                            onClick={() => openCxCModal(f)}
                            data-testid={`cxc-cell-${i}`}
                          >
                            {formatCLP(v)}
                          </td>
                        );
                      }
                      if (isCxPCell) {
                        return (
                          <td
                            key={i}
                            className={`border text-right tabular-nums ${cellPad} ${valueExtra || st.value} ${highlight} ${negClass} cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30 hover:ring-1 hover:ring-red-500 transition-colors`}
                            style={{ width: colW, minWidth: colW }}
                            title={`${formatCLPFull(v)} — Click para ver nómina del ${f.fecha}`}
                            onClick={() => setCxpModal({ open: true, fecha: f.fecha })}
                            data-testid={`cxp-cell-${i}`}
                          >
                            {formatCLP(v)}
                          </td>
                        );
                      }
                      if (!shiftable) {
                        // Iter 313.k.2 (Renzo, feb-2026): Fila 57 "Saldo Global" con
                        // franja de color según el "colchón" del día:
                        //   v > 30M   → verde (holgado)
                        //   0-30M     → ámbar (tenso)
                        //   v < 0     → rojo (déficit)
                        // Se aplica sobre celdas ya del tipo 'total' (branch abajo).
                        const isRow57 = fila.orden === 57;
                        const saldoGlobalTier = isRow57
                          ? (v < 0
                              ? '!bg-rose-100 dark:!bg-rose-900/50 !text-rose-800 dark:!text-rose-200 font-bold'
                              : v <= 30_000_000
                                ? '!bg-amber-100 dark:!bg-amber-900/40 !text-amber-800 dark:!text-amber-200 font-semibold'
                                : '!bg-emerald-100 dark:!bg-emerald-900/40 !text-emerald-800 dark:!text-emerald-200 font-semibold')
                          : '';
                        if (isSep || fila.tipo === 'total' || fila.tipo === 'banco') {
                          return (
                            <td
                              key={i}
                              className={`border text-right tabular-nums ${cellPad} ${isSep ? st.stickyBg : ''} ${isSep ? '' : (valueExtra || st.value)} ${isSep ? '' : highlight} ${negClass} ${saldoGlobalTier}`}
                              style={{ width: colW, minWidth: colW }}
                              title={v ? formatCLPFull(v) + (isRow57 ? (v < 0 ? ' — DÉFICIT' : v <= 30_000_000 ? ' — colchón bajo (<30M)' : ' — colchón holgado (>30M)') : '') : ''}
                              data-testid={isNegativeBelow40 ? `neg-cell-${fila.orden}-${i}` : undefined}
                            >
                              {isSep ? '' : formatCLP(v)}
                            </td>
                          );
                        }
                        return (
                          <td
                            key={i}
                            className={`border text-right tabular-nums ${cellPad} ${valueExtra || st.value} ${highlight} ${negClass}`}
                            style={{ width: colW, minWidth: colW }}
                            title={v ? formatCLPFull(v) : ''}
                            data-testid={isNegativeBelow40 ? `neg-cell-${fila.orden}-${i}` : undefined}
                          >
                            {formatCLP(v)}
                          </td>
                        );
                      }
                      return (
                        <ShiftableCell
                          key={i}
                          value={v}
                          fecha={f.fecha}
                          colWidth={colW}
                          className={`${cellPad} ${valueExtra || st.value} ${highlight} ${negClass}`}
                          onShift={(diasShift) => crearShift(fila.orden, f.fecha, diasShift, Math.abs(v), `${fila.nombre} ${formatCLP(v)}`)}
                          filaNombre={fila.nombre}
                        />
                      );
                    })}
                  </tr>
                );
              })}

              {/* Fila de total de egresos por día */}
              <tr className="bg-red-100 dark:bg-red-950/30 font-bold border-t-2 border-red-400" data-testid="row-total-egresos">
                <td className="sticky left-0 z-20 border border-r-2 border-r-red-400 px-1 py-0.5 bg-red-100 dark:bg-red-950 text-red-900 dark:text-red-200 text-center">Σ</td>
                <td className="sticky left-10 z-20 border px-1.5 py-0.5 bg-red-100 dark:bg-red-950 text-red-900 dark:text-red-200 truncate max-w-[192px]">TOTAL EGRESOS DÍA</td>
                <td className="sticky left-[202px] z-20 border px-1.5 py-0.5 bg-red-100 dark:bg-red-950 text-red-900 dark:text-red-200 text-right tabular-nums">
                  {formatCLP(total_egresos_dia.reduce((a, b) => a + b, 0))}
                </td>
                <td className="sticky left-[314px] z-20 border border-r-2 border-r-red-400 px-1.5 py-0.5 bg-red-100 dark:bg-red-950" />
                {total_egresos_dia.map((v, i) => (
                  <td key={i} className={`border px-0.5 py-0.5 text-right tabular-nums text-red-900 dark:text-red-200 ${fechas[i].es_hoy ? 'ring-2 ring-amber-500 ring-inset' : ''}`}
                      style={{ width: colW, minWidth: colW }}
                      title={v ? formatCLPFull(v) : ''}>
                    {formatCLP(v)}
                  </td>
                ))}
              </tr>

              {/* Fila de total de ingresos por día */}
              <tr className="bg-emerald-100 dark:bg-emerald-950/30 font-bold" data-testid="row-total-ingresos">
                <td className="sticky left-0 z-20 border border-r-2 border-r-emerald-400 px-1 py-0.5 bg-emerald-100 dark:bg-emerald-950 text-emerald-900 dark:text-emerald-200 text-center">Σ</td>
                <td className="sticky left-10 z-20 border px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-950 text-emerald-900 dark:text-emerald-200 truncate max-w-[192px]">TOTAL INGRESOS DÍA</td>
                <td className="sticky left-[202px] z-20 border px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-950 text-emerald-900 dark:text-emerald-200 text-right tabular-nums">
                  {formatCLP(total_ingresos_dia.reduce((a, b) => a + b, 0))}
                </td>
                <td className="sticky left-[314px] z-20 border border-r-2 border-r-emerald-400 px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-950" />
                {total_ingresos_dia.map((v, i) => (
                  <td key={i} className={`border px-0.5 py-0.5 text-right tabular-nums text-emerald-900 dark:text-emerald-200 ${fechas[i].es_hoy ? 'ring-2 ring-amber-500 ring-inset' : ''}`}
                      style={{ width: colW, minWidth: colW }}
                      title={v ? formatCLPFull(v) : ''}>
                    {formatCLP(v)}
                  </td>
                ))}
              </tr>

              {/* Fila saldo neto del día */}
              <tr className="bg-blue-100 dark:bg-blue-950/30 font-bold border-b-2 border-blue-400" data-testid="row-saldo-neto">
                <td className="sticky left-0 z-20 border border-r-2 border-r-blue-400 px-1 py-0.5 bg-blue-100 dark:bg-blue-950 text-blue-900 dark:text-blue-200 text-center">=</td>
                <td className="sticky left-10 z-20 border px-1.5 py-0.5 bg-blue-100 dark:bg-blue-950 text-blue-900 dark:text-blue-200 truncate max-w-[192px]">SALDO NETO DÍA</td>
                <td className="sticky left-[202px] z-20 border px-1.5 py-0.5 bg-blue-100 dark:bg-blue-950" />
                <td className="sticky left-[314px] z-20 border border-r-2 border-r-blue-400 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-950" />
                {saldoNeto.map((v, i) => (
                  <td key={i}
                      className={`border px-0.5 py-0.5 text-right tabular-nums ${v >= 0 ? 'text-emerald-800 dark:text-emerald-300' : 'text-red-800 dark:text-red-300'} ${fechas[i].es_hoy ? 'ring-2 ring-amber-500 ring-inset' : ''}`}
                      style={{ width: colW, minWidth: colW }}
                      title={v ? formatCLPFull(v) : ''}>
                    {formatCLP(v)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Hint */}
        <div className="flex items-start gap-2 px-4 py-2 border-t bg-muted/30 text-xs text-muted-foreground">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            Desplaza horizontalmente para ver los 91 días. Celdas vacías = $0. Primeras 4 columnas fijas. Montos en CLP con separador de miles.
            {' '}<strong>Clic en una celda con monto</strong> (egreso/ingreso) para moverla ±N días en el tiempo.
          </span>
        </div>
      </CardContent>

      {/* Dialog: Provisiones CRUD */}
      <Dialog open={openProvisiones} onOpenChange={handleCloseProvisiones}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="dialog-provisiones">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-amber-600" /> Provisiones (Fila 19 del Flujo)
            </DialogTitle>
          </DialogHeader>
          <ProvisionesPanel />
        </DialogContent>
      </Dialog>

      {/* Dialog: Detalle CxC al pinchar fila 43 */}
      <Dialog open={cxcModal.open} onOpenChange={(o) => setCxcModal(prev => ({ ...prev, open: o }))}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="dialog-cxc-detalle">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <ArrowDownCircle className="w-5 h-5 text-emerald-600" />
              Cuentas por Cobrar
              {cxcModal.fechas?.length === 1
                ? <span className="text-sm font-normal text-muted-foreground">— Vencen el {cxcModal.fecha}</span>
                : cxcModal.fechas?.length > 1
                  ? <span className="text-sm font-normal text-muted-foreground">— {cxcModal.fechas.length} días seleccionados</span>
                  : null}
            </DialogTitle>
          </DialogHeader>
          {cxcModal.loading ? (
            <div className="flex items-center justify-center p-6 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Cargando facturas…
            </div>
          ) : cxcModal.items.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              No hay CxC con vencimiento en este día.
            </div>
          ) : (
            <>
              <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded p-3 mb-3 flex items-center justify-between">
                <span className="text-xs text-emerald-800 dark:text-emerald-300">{cxcModal.items.length} factura(s)</span>
                <span className="font-bold text-emerald-900 dark:text-emerald-200 tabular-nums">
                  {formatCLPFull(cxcModal.items.reduce((s, c) => s + (c.monto || 0), 0))}
                </span>
              </div>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-100 dark:bg-slate-800">
                    <th className="border px-2 py-1.5 text-left">Folio</th>
                    <th className="border px-2 py-1.5 text-left">Cliente</th>
                    <th className="border px-2 py-1.5 text-right">Monto</th>
                    <th className="border px-2 py-1.5 text-center">Vence</th>
                    <th className="border px-2 py-1.5 text-center">Origen</th>
                    <th className="border px-2 py-1.5 text-center w-44">Mover a otra fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {cxcModal.items.map((c) => (
                    <tr key={c.id} className="hover:bg-slate-50 dark:hover:bg-slate-900" data-testid={`cxc-row-${c.id}`}>
                      <td className="border px-2 py-1 font-mono text-[11px]">{c.folio}</td>
                      <td className="border px-2 py-1 truncate max-w-[220px]" title={c.cliente}>{c.cliente}</td>
                      <td className="border px-2 py-1 text-right tabular-nums font-semibold">{formatCLP(c.monto)}</td>
                      <td className="border px-2 py-1 text-center">
                        {c.fecha_venc}
                        {c.fecha_venc !== c.fecha_venc_original && (
                          <div className="text-[9px] text-amber-600" title={`Original: ${c.fecha_venc_original}`}>
                            (movida)
                          </div>
                        )}
                      </td>
                      <td className="border px-2 py-1 text-center">
                        {c.fuente === 'alegra'
                          ? <Badge variant="outline" className="text-[9px] py-0 px-1 border-blue-400 text-blue-700">Alegra</Badge>
                          : <Badge variant="outline" className="text-[9px] py-0 px-1">Manual</Badge>}
                      </td>
                      <td className="border px-2 py-1 text-center">
                        <DatePickerInput
                          value={c.fecha_venc}
                          onChange={(newDate) => {
                            if (newDate && newDate !== c.fecha_venc) {
                              moverCxC(c.id, newDate);
                            }
                          }}
                          className="h-7 text-[10px] w-36"
                          data-testid={`cxc-date-${c.id}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[10px] text-muted-foreground mt-2">
                Cambia la fecha en la última columna para mover una factura.
                Si eliges sábado o domingo, se ajustará al lunes siguiente automáticamente.
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Facturas factorizadas vencidas (al click en saldo rojo de fila 50/53) */}
      <Dialog
        open={vencidasModal.open}
        onOpenChange={(o) => setVencidasModal(prev => ({ ...prev, open: o }))}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="dialog-factoring-vencidas">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <span className="w-2 h-2 rounded-full bg-red-600" />
              Facturas factorizadas vencidas
              <span className="text-sm font-normal text-muted-foreground">— {vencidasModal.lineaNombre}</span>
            </DialogTitle>
          </DialogHeader>
          {vencidasModal.loading ? (
            <div className="flex items-center justify-center p-6 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Cargando facturas vencidas…
            </div>
          ) : vencidasModal.items.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              No hay facturas vencidas en esta línea.
            </div>
          ) : (
            <>
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded p-3 mb-3 flex items-center justify-between">
                <span className="text-xs text-red-800 dark:text-red-300">
                  {vencidasModal.items.length} factura(s) vencida(s)
                </span>
                <span className="font-bold text-red-900 dark:text-red-200 tabular-nums">
                  {formatCLPFull(vencidasModal.totalVencido)}
                </span>
              </div>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-100 dark:bg-slate-800">
                    <th className="border px-2 py-1.5 text-left">Folio</th>
                    <th className="border px-2 py-1.5 text-left">Cliente</th>
                    <th className="border px-2 py-1.5 text-right">Monto Asignado</th>
                    <th className="border px-2 py-1.5 text-center">Vence</th>
                    <th className="border px-2 py-1.5 text-center">Días vencida</th>
                  </tr>
                </thead>
                <tbody>
                  {vencidasModal.items.map((a) => {
                    // Iter 387: vence según fecha de pago efectiva (modificada manualmente > Alegra)
                    const venc = a.fecha_pago_efectiva || a.fecha_venc || '';
                    let diasVencida = '-';
                    if (venc) {
                      const today = new Date();
                      const v = new Date(venc);
                      diasVencida = Math.max(0, Math.round((today - v) / 86400000));
                    }
                    return (
                      <tr key={a.id} className="hover:bg-slate-50 dark:hover:bg-slate-900" data-testid={`factoring-vencida-row-${a.id}`}>
                        <td className="border px-2 py-1 font-mono text-[11px]">{a.folio}</td>
                        <td className="border px-2 py-1 truncate max-w-[220px]" title={a.cliente}>{a.cliente}</td>
                        <td className="border px-2 py-1 text-right tabular-nums font-semibold text-red-700 dark:text-red-300">
                          {formatCLP(a.monto_asignado)}
                        </td>
                        <td className="border px-2 py-1 text-center text-red-700 dark:text-red-300">
                          {fmtFecha(venc)}
                          {a.fecha_pago_modificada && <span className="block text-[9px] text-muted-foreground" title={`Vencimiento Alegra: ${fmtFecha(a.fecha_venc)}`}>pago modificado</span>}
                        </td>
                        <td className="border px-2 py-1 text-center text-red-700 dark:text-red-300 font-semibold">
                          {diasVencida}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-[10px] text-muted-foreground mt-2">
                Para gestionar (marcar pagada, liberar, etc.), abre el panel de Factoring desde la barra superior.
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: Proyección 12 meses */}
      <Dialog open={openProyeccion} onOpenChange={handleCloseProyeccion}>
        <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="dialog-proyeccion-12m">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" /> Proyección a 12 Meses
            </DialogTitle>
          </DialogHeader>
          <Proyeccion12mPanel />
        </DialogContent>
      </Dialog>

      {/* Dialog: CxC Clientes */}
      <Dialog open={openCxC} onOpenChange={handleCloseCxC}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="dialog-cxc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowDownLeft className="w-5 h-5 text-indigo-600" /> Cuentas por Cobrar (Clientes)
            </DialogTitle>
          </DialogHeader>
          <CxCPanel />
        </DialogContent>
      </Dialog>

      {/* Dialog: CxP / Nómina del día (drill-down desde celda fila 2) */}
      <Dialog open={cxpModal.open} onOpenChange={(o) => setCxpModal(prev => ({ ...prev, open: o }))}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="dialog-cxp-grid">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-red-600" />
              Nómina de pago — {cxpModal.fecha || 'detalle'}
            </DialogTitle>
          </DialogHeader>
          <CxPPanel initialFilterDate={cxpModal.fecha} />
        </DialogContent>
      </Dialog>

      {/* Dialog: Vencimientos */}
      <Dialog open={openVencimientos} onOpenChange={setOpenVencimientos}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="dialog-vencimientos">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" /> Vencimientos Pendientes
            </DialogTitle>
          </DialogHeader>
          <VencimientosPanel />
        </DialogContent>
      </Dialog>

      {/* Dialog: Factoring */}
      <Dialog open={openFactoring} onOpenChange={handleCloseFactoring}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="dialog-factoring">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-purple-600" /> Líneas de Factoring
            </DialogTitle>
          </DialogHeader>
          <FactoringPanel />
        </DialogContent>
      </Dialog>

      {/* Modal: Auditoría de Fechas Modificadas */}
      <FechasModificadasModal
        open={fechasModalOpen}
        onClose={() => setFechasModalOpen(false)}
      />
    </Card>
  );
}
